/*
 * main-cart.js
 * -----------------------------------------------------------------------------
 * 独立购物车页(/cart, 非 drawer)专用逻辑。
 *
 * 背景 / 为什么需要这个文件:
 *  - 本站 settings.cart_type === 'drawer', 所以 cart-drawer 会在 /cart 页也全局渲染
 *    (layout/theme.liquid)。同时新的 main-cart section 把「商品列表」和「订单汇总」
 *    合并进同一个 section, 于是同一个 section 文档里存在两个刷新区块。
 *  - Dawn 原生 assets/cart.js 的 CartItems 在这种场景下有三个问题:
 *      1) getSectionInnerHTML 用 querySelector('.js-contents') 只取「第一个」, 两个
 *         刷新区块会串味;
 *      2) updateQuantity 结尾有一段 focus-trap 逻辑, 一旦页面上存在 <cart-drawer>
 *         就会把焦点陷阱绑到隐藏的抽屉上(a11y bug);
 *      3) onCartUpdate 里写死了老 section 名 'main-cart-items'。
 *  - 因此这里用一个继承 CartItems 的子类, 覆写相关方法, 完全不改动共享的 cart.js,
 *    也不影响 drawer。cart.js 必须先于本文件加载(section 里已保证顺序)。
 *
 * 依赖(均为 cart.js / global.js / constants.js 提供的全局): CartItems, fetchConfig,
 *  PUB_SUB_EVENTS, publish, routes, window.cartStrings。
 */

if (
  typeof CartItems !== 'undefined' &&
  !customElements.get('main-cart-items')
) {
  /**
   * 删除按钮。cart.js 的 CartRemoveButton 用 closest('cart-items') 找容器,
   * 找不到本页的 <main-cart-items>, 故为本页单独定义一个绑到 <main-cart-items> 的版本。
   */
  if (!customElements.get('main-cart-remove-button')) {
    class MainCartRemoveButton extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('click', (event) => {
          event.preventDefault();
          const cartItems = this.closest('main-cart-items');
          if (cartItems) cartItems.updateQuantity(this.dataset.index, 0);
        });
      }
    }
    customElements.define('main-cart-remove-button', MainCartRemoveButton);
  }

  class MainCartItems extends CartItems {
    /**
     * 初始化时, 除父类逻辑外, 建立移动端底部固定操作栏的显隐监测。
     */
    connectedCallback() {
      if (typeof super.connectedCallback === 'function') {
        super.connectedCallback();
      }
      this.setupStickyBar();
    }

    disconnectedCallback() {
      if (typeof super.disconnectedCallback === 'function') {
        super.disconnectedCallback();
      }
      if (this._stickyObserver) {
        this._stickyObserver.disconnect();
        this._stickyObserver = null;
      }
    }

    /**
     * 移动端底部固定操作栏: 当页面内的 checkout 按钮(#checkout)滚出视口时,
     * 在底部浮出 .mc-sticky-bar; 可见时隐藏。用 IntersectionObserver 实现。
     * 汇总区 AJAX 刷新后 #checkout / .mc-sticky-bar 会被重建, 故需重新绑定。
     */
    setupStickyBar() {
      if (this._stickyObserver) {
        this._stickyObserver.disconnect();
        this._stickyObserver = null;
      }
      const sentinel = this.querySelector('#checkout');
      const bar = this.querySelector('[data-mc-sticky-bar]');
      if (!sentinel || !bar) return;

      const toggle = (checkoutVisible) => {
        bar.classList.toggle('is-visible', !checkoutVisible);
        this.classList.toggle('mc-has-sticky', !checkoutVisible);
      };

      this._stickyObserver = new IntersectionObserver(
        (entries) => toggle(entries[0].isIntersecting),
        { root: null, threshold: 0 }
      );
      this._stickyObserver.observe(sentinel);
    }

    /**
     * 两个刷新目标, 用「各自独立」的选择器, 避免两个刷新区块混淆:
     *  - 商品列表:  #main-cart-items .js-contents-items
     *  - 汇总面板:  #main-cart-footer .js-contents-summary
     * 二者同属新 section(section id 相同), 各自抽取对应片段。
     */
    getSectionsToRender() {
      const sectionId = this.dataset.sectionId;
      return [
        {
          id: 'main-cart-items',
          section: sectionId,
          selector: '.js-contents-items',
        },
        {
          id: 'main-cart-footer',
          section: sectionId,
          selector: '.js-contents-summary',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
        {
          id: 'cart-live-region-text',
          section: 'cart-live-region-text',
          selector: '.shopify-section',
        },
      ];
    }

    /**
     * 收到「非自身发起」的 cartUpdate(例如产品页加购、care-buy 等 publish 的事件)时,
     * 重新拉取本 section 的 HTML 并替换两个刷新区块。覆写原方法里写死的
     * '?section_id=main-cart-items'。
     */
    onCartUpdate() {
      const sectionId = this.dataset.sectionId;
      fetch(`${routes.cart_url}?section_id=${sectionId}`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            'text/html'
          );
          ['.js-contents-items', '.js-contents-summary'].forEach((selector) => {
            const target = document.querySelector(selector);
            const source = html.querySelector(selector);
            if (target && source) target.innerHTML = source.innerHTML;
          });
          this.setupStickyBar();
        })
        .catch((e) => console.error(e));
    }

    /**
     * 按传入 selector 精确抽取 section 渲染结果的 innerHTML。
     * 覆写父类版本(父类默认只处理单一 .js-contents)。
     */
    getSectionInnerHTML(html, selector) {
      const node = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector);
      return node ? node.innerHTML : '';
    }

    /**
     * 改数量 / 删除行。改写自 cart.js 的 CartItems.updateQuantity,
     * 去掉了所有 cart-drawer 相关的焦点陷阱分支(本页不需要, 且 drawer 在本页隐藏)。
     */
    updateQuantity(line, quantity, name, variantId) {
      this.enableLoading(line);

      const body = JSON.stringify({
        line,
        quantity,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      });

      fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
        .then((response) => response.text())
        .then((state) => {
          const parsedState = JSON.parse(state);
          const quantityElement = document.getElementById(`Quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            if (quantityElement) {
              quantityElement.value = quantityElement.getAttribute('value');
            }
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartFooter = document.getElementById('main-cart-footer');
          if (cartFooter) {
            cartFooter.classList.toggle(
              'is-empty',
              parsedState.item_count === 0
            );
          }

          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document
                .getElementById(section.id)
                .querySelector(section.selector) ||
              document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });

          const updatedValue = parsedState.items[line - 1]
            ? parsedState.items[line - 1].quantity
            : undefined;
          let message = '';
          if (
            items.length === parsedState.items.length &&
            updatedValue !== parseInt(quantityElement.value)
          ) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace(
                '[quantity]',
                updatedValue
              );
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem = document.getElementById(`CartItem-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            lineItem.querySelector(`[name="${name}"]`).focus();
          }

          this.setupStickyBar();

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-items',
            cartData: parsedState,
            variantId: variantId,
          });
        })
        .catch(() => {
          this.querySelectorAll('.loading-overlay').forEach((overlay) =>
            overlay.classList.add('hidden')
          );
          const errors = document.getElementById('cart-errors');
          if (errors) errors.textContent = window.cartStrings.error;
        })
        .finally(() => {
          this.disableLoading(line);
        });
    }
  }

  customElements.define('main-cart-items', MainCartItems);
}

/**
 * [data-mc-back] Continue shopping 按钮:
 * referrer 是 hoverair.com 同站且有历史可回退 → history.back()
 * 否则走 href 兜底(不 preventDefault)
 */
// document.addEventListener('click', function (e) {
//   const btn = e.target.closest('[data-mc-back]');
//   if (!btn) return;
//   let sameSite = false;
//   const ref = document.referrer;
//   if (ref) {
//     try {
//       const host = new URL(ref).hostname;
//       // sameSite = host === window.location.hostname;
//       sameSite = host === 'hoverair.com'
//         || host.endsWith('.hoverair.com')
//         || host === window.location.hostname; // 兼容本地开发(127.0.0.1 / localhost)
//     } catch (_) {}
//   }
//   console.log("sameSite && history.length", sameSite && history.length);
//   if (sameSite && history.length > 1) {
//     e.preventDefault();
//     history.back();
//   }
// });
